import { Link } from "@tanstack/react-router";
import { AuthShell } from "../../components/auth/AuthShell";
import { getAppHost, otherAppUrl } from "../../lib/appHost";
import { Button } from "@/components/ui/button";

/**
 * AUTH-03 — AMCaaS (B2C) **platform** login: https://amcaas.app/login/platform.
 *
 * Door for self-service platform users. A fresh user has zero providers and
 * lands on the marketplace (B2C-APP-06, OS-05); the org switcher appears only
 * after their first subscription (OS-03). Self-registration is the primary CTA.
 * Neutral, app default theme only (WL-04).
 *
 * Host IS a trust boundary (AUTH-06): the platform door only exists on the AMCaaS
 * (B2C) app. Opening it directly on the business app (e.g. `localhost` without
 * `?app=amcaas`) is rejected — it must be reached from the marketplace app.
 */
export function AmcaasPlatformLoginPage(): JSX.Element {
  if (getAppHost() !== "b2c") return <PlatformLoginWrongHost />;
  return (
    <AuthShell
      appName="AMCaaS"
      appTagline="Application Maintenance Contracts as a Service"
      logoSrc="/logos/amcaas-login-banner.png"
      logoSrc2x="/logos/amcaas-login-banner@2x.png"
      accent="#7c3aed"
      panelClassName="bg-gradient-to-br from-zinc-950 via-zinc-900 to-violet-950"
      audienceLabel="Platform sign-in"
      headline="Turn every warranty into revenue. Stop chasing renewals. Start owning them."
      blurb="From catalogue to renewal, run your entire AMC business in one place — reach new consumers through the marketplace without losing control of your direct relationships.
Discover and subscribe to AMC plans in minutes, track every asset you own, and never miss a renewal again — all from one place."
      quote={{
        text: "I found the tools I needed and subscribed in one afternoon — no sales calls.",
        author: "Dana Okoye, Independent Consultant",
      }}
      footer={
        <div className="space-y-3">
          <p>
            New to AMCaaS?{" "}
            <Link
              to="/register"
              className="font-medium text-foreground hover:underline"
            >
              Create an account
            </Link>
          </p>
          <p>
            Are you a provider?{" "}
            <Link
              to="/login/provider"
              className="font-medium text-foreground hover:underline"
            >
              Provider sign-in
            </Link>
          </p>
        </div>
      }
    />
  );
}

/** Shown when the marketplace platform door is opened on the business app. */
function PlatformLoginWrongHost(): JSX.Element {
  const url = otherAppUrl("/login/platform");
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 p-10 text-center">
      <img
        src="/Logo.svg"
        alt="AMSaaS"
        className="w-40 max-w-full object-contain"
      />
      <h1 className="text-lg font-semibold">
        Marketplace sign-in isn’t available on the business app
      </h1>
      <p className="max-w-md text-sm text-muted-foreground">
        Platform (marketplace) accounts sign in on the AMCaaS app.{" "}
        {url
          ? "Continue there to sign in."
          : "Open the AMCaaS app (or add ?app=amcaas in local dev) to sign in."}
      </p>
      <div className="flex gap-2">
        {url && (
          <Button asChild>
            <a href={url}>Go to the marketplace app</a>
          </Button>
        )}
        <Button asChild variant="outline">
          <Link to="/login/provider">Provider sign-in</Link>
        </Button>
      </div>
    </div>
  );
}
